import dagre from 'dagre';
// ELK: usar import dinámico de la versión bundled (sin worker) para evitar issues con CRA/Webpack
import { STATUS } from './careerLogic';

// Mejoradas constantes para un diseño más atractivo
export const NODE_WIDTH = 240;  // Más ancho para mejor lectura
export const NODE_HEIGHT = 100;  // Más alto para mejor diseño

// Colores y estilos mejorados para diferentes tipos de nodos
export const NODE_STYLES = {
  CBC: {
    backgroundColor: 'var(--bg-secondary)',
    borderColor: 'var(--border-color)',
    borderWidth: '2px',
    borderStyle: 'dashed',
    opacity: 0.8
  },
  CPC: {
    backgroundColor: 'var(--status-cursando-bg)',
    borderColor: 'var(--status-cursando-border)',
    borderWidth: '2px',
    borderStyle: 'solid'
  },
  CPO_COMUN: {
    backgroundColor: 'var(--status-disponible-bg)',
    borderColor: 'var(--status-disponible-border)',
    borderWidth: '2px',
    borderStyle: 'solid'
  },
  CPO_ORIENTACION: {
    backgroundColor: 'var(--status-cursada-bg)',
    borderColor: 'var(--status-cursada-border)',
    borderWidth: '2px',
    borderStyle: 'solid'
  }
};

// Configuración mejorada para diferentes tipos de conexiones
export const EDGE_STYLES = {
  DEFAULT: {
    strokeWidth: 2,
    stroke: 'var(--rf-edge-color)',
    opacity: 0.6
  },
  PREREQUISITO: {
    strokeWidth: 3,
    stroke: 'var(--status-puede-cursar-border)',
    strokeDasharray: '8,4',
    opacity: 0.9
  },
  HABILITADA: {
    strokeWidth: 3,
    stroke: 'var(--status-cursando-border)',
    strokeDasharray: '12,4',
    opacity: 0.9
  },
  STRONG: {
    strokeWidth: 4,
    stroke: 'var(--accent-color)',
    opacity: 1
  }
};

// Función mejorada para determinar el nivel de una materia
const determinarNivelMateria = (materia, plan) => {
  if (!materia) return 0;
  
  // CBC = Nivel 0
  if (materia.id && materia.id.startsWith('cbc_')) return 0;
  
  // CPC = Niveles 1-4 (aproximadamente)
  if (materia.categoria && materia.categoria.includes('CPC')) {
    // Estimar nivel basado en correlativas
    const correlativasCount = materia.correlativas ? materia.correlativas.length : 0;
    return Math.min(4, Math.floor(correlativasCount / 2) + 1);
  }
  
  // CPO = Niveles 5-8
  if (materia.categoria && materia.categoria.includes('CPO')) {
    const correlativasCount = materia.correlativas ? materia.correlativas.length : 0;
    return Math.min(8, Math.max(5, Math.floor(correlativasCount / 2) + 3));
  }
  
  // Fallback basado en correlativas
  const correlativasCount = materia.correlativas ? materia.correlativas.length : 0;
  return Math.min(8, Math.floor(correlativasCount / 3));
};

// Genera nodos FILTRADOS según la vista actual (orientación, etc.)
export const generarNodosParaVista = (plan, materias, options) => {
  if (!plan || !plan.grupos || !materias) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error('generarNodosParaVista: Missing required data');
    }
    return [];
  }

  const {
    showCBC = false, // por defecto ya no se muestran
    showCPO = true,
    materiaSeleccionadaParaExplorar,
    prerrequisitosExplorar,
    habilitadasExplorar,
    orientacionSeleccionada,
    onMateriaChangeStatus,
    onNotaChange,
    onExplorarMateria,
    onAprobarTodasCBC,
    cbcExpanded,
    onToggleCbcExpanded,
    cbcAprobadoFlag = false
  } = options;

  const nodos = [];
  const selectionActive = Boolean(materiaSeleccionadaParaExplorar);

  // Calcular estado de CBC aprobado (todas las CBC marcadas o flag)
  const materiasCBCIds = ['cbc_1', 'cbc_2', 'cbc_3', 'cbc_4', 'cbc_5', 'cbc_6'];
  const materiasCBC = materiasCBCIds.map(id => materias[id]).filter(Boolean);
  const cbcAprobado = cbcAprobadoFlag || (materiasCBC.length === 6 && materiasCBC.every(m =>
    m.estado === STATUS.CURSADA_APROBADA || m.notaFinal === -3
  ));

  // Agregar nodo CBC Master si está colapsado
  // Si decidimos NUNCA mostrar CBC, no agregamos nodo CBC Master ni individuales

  // Procesar grupos de materias
  plan.grupos.forEach((grupo) => {
    if (!grupo.materias) return;

    grupo.materias.forEach((materia) => {
      // Verificar si es CBC y omitirlos por completo
      const isCBC = materia.id && materia.id.startsWith('cbc_');
      if (isCBC) {
        return; // no renderizar CBC
      }
      const hidden = false;
      
      // Omitir materias CPO si no se muestran (solo CPO, no CBC)
      if (!showCPO && materia.categoria && materia.categoria.includes('CPO') && !isCBC) {
         if (process.env.NODE_ENV !== 'production') {
           // eslint-disable-next-line no-console
           console.log('⏭️ Omitiendo materia CPO:', materia.id);
         }
        return;
      }

      const materiaConEstado = materias[materia.id];
      if (!materiaConEstado) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('⚠️ Materia sin estado:', materia.id);
        }
        return;
      }

      // Filtrar por orientación si está seleccionada (solo para CPO)
      let debeMostrar = true;
      if (orientacionSeleccionada && materia.categoria && materia.categoria.includes('CPO')) {
        const esDeOrientacion = materia.categoria.includes(orientacionSeleccionada.nombre);
        const esComun = materia.categoria.includes('Común');
        debeMostrar = esComun || esDeOrientacion;
      }

      if (!debeMostrar) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('⏭️ Omitiendo materia por orientación:', materia.id);
        }
        return;
      }

      // Crear nodo - TODOS los nodos se crean, solo los CBC pueden estar hidden
      const nodo = {
        id: materia.id,
        type: 'subject',
        position: { x: 0, y: 0 },
        data: {
          materia: materiaConEstado,
          onMateriaChangeStatus,
          onNotaChange,
          onExplorarMateria,
          onShowInfo: options.onShowInfo,
          esMateriaSeleccionadaParaExplorar: materiaSeleccionadaParaExplorar === materia.id,
          esPrerrequisitoDeExplorada: prerrequisitosExplorar.has(materia.id),
          esHabilitadaPorExplorada: habilitadasExplorar.has(materia.id),
          selectionActive,
          hidden,
          isCBC,
          cbcAprobado,
          recentlyChangedIds: options.recentlyChangedIds || new Set()
        },
        width: NODE_WIDTH,
        height: NODE_HEIGHT
      };

      // Rank por ciclo para layout: CBC=0, CPC=1, CPO=2
      let rank = 1;
      if (materia.categoria === 'CBC') rank = 0;
      else if (String(materia.categoria || '').startsWith('CPO')) rank = 2;
      nodo.data.rank = rank;

      // Solo agregar la prop hidden si es true (para ReactFlow)
      if (hidden) {
        nodo.hidden = true;
      }

      nodos.push(nodo);
      

    });
  });

  return nodos;
};

export const generarEdgesParaNodosVisibles = (nodos, materias, options = {}) => {
  if (!nodos || !materias) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('❌ generarEdgesParaNodosVisibles: Missing data');
    }
    return [];
  }

  const edges = [];
  const edgeIds = new Set(); // Prevenir edges duplicados
  const nodosIds = new Set(nodos.map(n => n.id));
  
  const selectedId = options.selectedId || null;
  const reqSet = options.reqSet || new Set();
  const unlockSet = options.unlockSet || new Set();
  const selectionActive = Boolean(selectedId);

  const createEdge = (source, target) => {
    const edgeId = `e${source}-${target}`;
    
    // Prevenir edges duplicados
    if (edgeIds.has(edgeId)) {
      return null;
    }
    edgeIds.add(edgeId);
    
    const sourceMateria = materias[source];
    const targetMateria = materias[target];
    
    if (!sourceMateria || !targetMateria) {
      return null;
    }

    // Determinar el tipo de conexión, priorizando semántica con selección activa
    let edgeType = 'default';
    let className = '';
    const isPrereqRelation = targetMateria.correlativas && targetMateria.correlativas.includes(source);
    
    if (selectionActive) {
      const sourceInReq = reqSet.has(source);
      const targetInReqOrSelected = reqSet.has(target) || target === selectedId;
      const sourceInUnlockPath = source === selectedId || (unlockSet.has(source) && unlockSet.has(target));
      
      if (isPrereqRelation && sourceInReq && targetInReqOrSelected) {
        edgeType = 'req';
        className = 'edge-req';
      } else if (source === selectedId && unlockSet.has(target)) {
        edgeType = 'unlock';
        className = 'edge-unlock';
      } else {
        edgeType = 'muted';
        className = 'edge-muted';
      }
    } else {
      if ((sourceMateria.estado === STATUS.CURSADA_APROBADA || sourceMateria.notaFinal === -3) && 
          targetMateria.puedeCursar) {
        edgeType = 'habilitada';
        className = 'edge-habilitada';
      } else if (isPrereqRelation) {
        edgeType = 'prerequisito';
        className = 'edge-prerequisito';
      }
    }

    return {
      id: edgeId,
      source,
      target,
      type: 'step',
      className,
      style: {
        strokeWidth: edgeType === 'muted' || edgeType === 'default' ? 2 : 3,
        stroke: edgeType === 'unlock' || edgeType === 'habilitada' ? '#22c55e' : 
                edgeType === 'req' || edgeType === 'prerequisito' ? '#f59e0b' : 
                '#9ca3af',
        // Líneas sólidas por defecto; dash se podría usar para "pendiente"
        opacity: edgeType === 'muted' ? 0.25 : (edgeType === 'default' ? 0.6 : 0.95)
      },
      markerEnd: {
        type: 'arrowclosed',
        width: 18,
        height: 18,
        color: edgeType === 'unlock' || edgeType === 'habilitada' ? '#22c55e' : 
               edgeType === 'req' || edgeType === 'prerequisito' ? '#f59e0b' : 
               '#9ca3af',
      }
    };
  };

  // Generar conexiones entre nodos visibles
  nodos.forEach(nodo => {
    const materia = materias[nodo.id];
    if (!materia) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('⚠️ Materia no encontrada para nodo:', nodo.id);
      }
      return;
    }

    // Conexiones de correlativas
    if (materia.correlativas) {
      materia.correlativas.forEach(correlativaId => {
        if (nodosIds.has(correlativaId)) {
          const edge = createEdge(correlativaId, nodo.id);
          if (edge) {
            edges.push(edge);
    
          }
        }
      });
    }

    // Conexiones de habilitación (materias que habilitan a otras)
    nodos.forEach(nodoDestino => {
      const materiaDestino = materias[nodoDestino.id];
      if (materiaDestino && materiaDestino.correlativas && materiaDestino.correlativas.includes(nodo.id)) {
        const edge = createEdge(nodo.id, nodoDestino.id);
        if (edge) {
          edges.push(edge);
          
        }
      }
    });
  });

  // Filtro adicional para eliminar duplicados por ID
  const uniqueEdges = edges.filter(
    (edge, index, self) =>
      index === self.findIndex(e => e.id === edge.id)
  );

  return uniqueEdges;
};

// Layout ortogonal con ELK (layered, top->down)
export const getLayoutWithELK = async (nodes, edges) => {
  if (!nodes || nodes.length === 0) {
    return { nodes: [], edges: edges || [] };
  }
  const { default: ELK } = await import('elkjs/lib/elk.bundled.js');
  const elk = new ELK();
  const elkGraph = {
    id: 'root',
    layoutOptions: {
      'elk.algorithm': 'layered',
      'elk.direction': 'DOWN',
      'elk.edgeRouting': 'ORTHOGONAL',
      'elk.layered.nodePlacement.favorStraightEdges': 'true',
      'elk.spacing.nodeNode': '60',
      'elk.spacing.edgeEdge': '30',
      'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
    },
    children: nodes.map((n) => ({
      id: String(n.id),
      width: n.width ?? NODE_WIDTH,
      height: n.height ?? NODE_HEIGHT,
      layoutOptions: {
        ...(Number.isFinite(n.data?.rank) ? { 'elk.layered.priority': String(10 - n.data.rank) } : {}),
      },
    })),
    edges: (edges || []).map((e) => ({ id: String(e.id), sources: [String(e.source)], targets: [String(e.target)] })),
  };

  try {
    const res = await elk.layout(elkGraph);
    const posById = Object.fromEntries(
      (res.children || []).map((c) => [String(c.id), { x: c.x || 0, y: c.y || 0 }])
    );
    const layoutedNodes = nodes.map((n) => ({
      ...n,
      position: posById[String(n.id)] || n.position || { x: 0, y: 0 },
      draggable: false,
    }));
    return { nodes: layoutedNodes, edges };
  } catch (e) {
    // Fallback: devolver sin cambios
    return { nodes, edges };
  }
};

export const getLayoutedElements = (nodes, edges) => {
  if (!nodes || nodes.length === 0) {
    return { nodes: [], edges: edges || [] };
  }

  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));

  // Configuración del layout con orientación Top -> Bottom (jerárquico)
  dagreGraph.setGraph({
    rankdir: 'TB',           // Top to Bottom
    align: 'UL',
    nodesep: 120,            // Separación horizontal entre nodos del mismo rango
    ranksep: 180,            // Separación vertical entre rangos (niveles)
    marginx: 80,
    marginy: 120,
    acyclicer: 'greedy',     // Mejor manejo de ciclos
    ranker: 'network-simplex' // Algoritmo optimizado
  });

  // Calcular dimensiones de nodos mejoradas
  const nodeWidth = NODE_WIDTH;
  const nodeHeight = NODE_HEIGHT;

  // Añadir nodos con información de nivel mejorada
  nodes.forEach((node) => {
    const nivel = node.data?.materia ? determinarNivelMateria(node.data.materia) : 0;
    
    dagreGraph.setNode(node.id, {
      width: nodeWidth,
      height: nodeHeight,
      rank: nivel  // Usar el nivel calculado para mejor organización
    });
  });

  // Añadir edges con pesos para mejor organización
  if (edges && edges.length > 0) {
    edges.forEach((edge) => {
      dagreGraph.setEdge(edge.source, edge.target, {
        weight: edge.className === 'edge-prerequisito' ? 2 : 1  // Dar más peso a prerequisitos
      });
    });
  }

  try {
    // Calcular layout con manejo de errores mejorado
    dagre.layout(dagreGraph);

    // Obtener posiciones con transformaciones mejoradas
    const layoutedNodes = nodes.map((node) => {
      const nodeWithPosition = dagreGraph.node(node.id);
      
      if (!nodeWithPosition) {
        return {
          ...node,
          position: { x: 0, y: 0 }  // Posición fallback
        };
      }

      // Aplicar transformaciones para mejor distribución visual
      const nivel = node.data?.materia ? determinarNivelMateria(node.data.materia) : 0;
      
      // PARCHE: Asegurar que las posiciones no sean NaN
      const safeX = isNaN(nodeWithPosition.x) ? 0 : nodeWithPosition.x;
      const safeY = isNaN(nodeWithPosition.y) ? 0 : nodeWithPosition.y;
      
      const layoutedNode = {
        ...node,
        position: {
          x: safeX - nodeWidth / 2,
          y: safeY - nodeHeight / 2
        },
        style: {
          ...node.style,
          zIndex: 100 - nivel,  // Nodos de nivel superior tienen mayor z-index
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',  // Animación suave
        },
        // Añadimos datos adicionales para animaciones
        data: {
          ...node.data,
          nivel: nivel,
          posicionCalculada: true
        }
      };

      return layoutedNode;
    });

    // Mejorar edges con estilos y animaciones
    const layoutedEdges = edges ? edges.map((edge) => ({
      ...edge,
      style: {
        ...edge.style,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
      }
    })) : [];

    return { nodes: layoutedNodes, edges: layoutedEdges };
  } catch (error) {
    console.error('💥 Error en getLayoutedElements:', error);
    return { nodes: nodes, edges: edges || [] };
  }
};